import { EditedMetrics } from "../../components/tables/DriverMetricsTableBody";
import { AnalysisDriverType } from "../../types/types";
import {
  DriverTableSegment,
  formatCombinedDriverTableData,
  formatDriverTableData,
  sortCombinedDriverRows,
  sortDriverRows,
} from "../driverTableUtils";
import {
  formatHeadroomCombinedTableData,
  formatHeadroomTableData,
  HeadroomTableSegment,
  sortHeadroomCombinedRows,
  sortHeadroomRows,
} from "../headroomTableUtils";
import { formatMetric } from "../metricUtils";
import {
  TableSegment,
  sortCombinedRows,
  sortRows,
  formatTableData,
  formatCombinedTableData,
} from "../segmentationTableUtils";
import { DriverMetricsResponse, SegmentResponse, HeadroomFinalResponse } from "@/graphql/types/graphql";

export interface EditedPercentiles {
  [uuid: string]: {
    driver1Percentile?: number;
    driver2Percentile?: number;
  };
}

/**
 * Builds row data for the Excel worksheet.
 * @param segmentsToExport - Array of table segments to export.
 * @param data - Array of SegmentResponse data from the backend.
 * @param driver1Mode - Analysis mode for the first driver.
 * @param driver2Mode - Analysis mode for the second driver.
 * @param sortColumn - Column to sort by (default: "range").
 * @param sortDirection - Sorting direction (default: "asc").
 * @returns Array of row data.
 */
export const buildRowData = (
  segmentsToExport: TableSegment[],
  data: SegmentResponse[] | undefined,
  driver1Mode: AnalysisDriverType,
  driver2Mode: AnalysisDriverType,
  sortColumn: string = "range",
  sortDirection: "asc" | "desc" = "asc"
): (string | number)[][] => {
  const worksheetData: (string | number)[][] = [];
  const hasOneDataAndOneManualSegment =
    segmentsToExport.length === 2 &&
    segmentsToExport.some((seg) => seg.method === "DATA") &&
    segmentsToExport.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segmentsToExport.every((seg) => seg.method === "DATA");
  const isTwoSameMethod =
    segmentsToExport.length === 2 && segmentsToExport.every((seg) => seg.method === segmentsToExport[0].method);

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatCombinedTableData(segmentsToExport, data);
    const sortedCombinedRows = sortCombinedRows(combinedRows, sortColumn, sortDirection);
    sortedCombinedRows.forEach((row) => {
      const rowData: (string | number)[] = [];
      rowData.push(row.manualRange || "");
      rowData.push(row.dataValue || "");
      rowData.push(row.data?.total ?? "");
      rowData.push(row.data?.count1 ?? "");
      rowData.push(row.data?.count2 ?? "");
      rowData.push(formatMetric(row.data?.driver1Metrics?.min, driver1Mode));
      rowData.push(formatMetric(row.data?.driver1Metrics?.percentile25, driver1Mode));
      rowData.push(formatMetric(row.data?.driver1Metrics?.percentile50, driver1Mode));
      rowData.push(formatMetric(row.data?.driver1Metrics?.percentile75, driver1Mode));
      rowData.push(formatMetric(row.data?.driver1Metrics?.max, driver1Mode));
      rowData.push(formatMetric(row.data?.driver2Metrics?.min, driver2Mode));
      rowData.push(formatMetric(row.data?.driver2Metrics?.percentile25, driver2Mode));
      rowData.push(formatMetric(row.data?.driver2Metrics?.percentile50, driver2Mode));
      rowData.push(formatMetric(row.data?.driver2Metrics?.percentile75, driver2Mode));
      rowData.push(formatMetric(row.data?.driver2Metrics?.max, driver2Mode));
      worksheetData.push(rowData);
    });
  } else {
    const { rows } = formatTableData(
      segmentsToExport,
      data || segmentsToExport.flatMap((seg) => seg.ranges.map((r) => r.data).filter((d): d is SegmentResponse => !!d))
    );
    const sortedRows = sortRows(rows, sortColumn, sortDirection, isDataOnly);
    sortedRows.forEach((rowSegments) => {
      const row: (string | number)[] = [];
      const rangeData = rowSegments.find((r) => r.data);
      if (isManualOnly) {
        row.push("Manual");
        row.push(rowSegments[0].segmentValue ?? "");
      } else if (isDataOnly && segmentsToExport.length === 1) {
        row.push("Data");
        row.push(rowSegments[0].segmentValue ?? "");
      } else if (isTwoSameMethod) {
        const firstSegmentValue =
          rowSegments.find((r) => r.segmentIndex === segmentsToExport[0].index)?.segmentValue || "";
        const secondSegmentValue =
          rowSegments.find((r) => r.segmentIndex === segmentsToExport[1].index)?.segmentValue || "";
        row.push(firstSegmentValue);
        row.push(secondSegmentValue);
      } else {
        rowSegments.forEach((range) => {
          row.push(range.range ?? "");
          row.push(range.segmentValue ?? "");
        });
      }
      row.push(rangeData?.data?.total ?? "");
      row.push(rangeData?.data?.count1 ?? "");
      row.push(rangeData?.data?.count2 ?? "");
      row.push(formatMetric(rangeData?.data?.driver1Metrics?.min, driver1Mode));
      row.push(formatMetric(rangeData?.data?.driver1Metrics?.percentile25, driver1Mode));
      row.push(formatMetric(rangeData?.data?.driver1Metrics?.percentile50, driver1Mode));
      row.push(formatMetric(rangeData?.data?.driver1Metrics?.percentile75, driver1Mode));
      row.push(formatMetric(rangeData?.data?.driver1Metrics?.max, driver1Mode));
      row.push(formatMetric(rangeData?.data?.driver2Metrics?.min, driver2Mode));
      row.push(formatMetric(rangeData?.data?.driver2Metrics?.percentile25, driver2Mode));
      row.push(formatMetric(rangeData?.data?.driver2Metrics?.percentile50, driver2Mode));
      row.push(formatMetric(rangeData?.data?.driver2Metrics?.percentile75, driver2Mode));
      row.push(formatMetric(rangeData?.data?.driver2Metrics?.max, driver2Mode));
      worksheetData.push(row);
    });
  }
  return worksheetData;
};

/**
 * Builds row data for the Excel worksheet for a single driver's metrics.
 */
export const buildDriverRowData = (
  segmentsToExport: DriverTableSegment[],
  data: DriverMetricsResponse[] | undefined,
  driverMode: AnalysisDriverType,
  headroomDriver: "driver1" | "driver2",
  sortColumn: string = "range",
  sortDirection: "asc" | "desc" = "asc",
  editedMetrics: EditedMetrics = {}
): (string | number)[][] => {
  const worksheetData: (string | number)[][] = [];
  const hasOneDataAndOneManualSegment =
    segmentsToExport.length === 2 &&
    segmentsToExport.some((seg) => seg.method === "DATA") &&
    segmentsToExport.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segmentsToExport.length === 1 && segmentsToExport.every((seg) => seg.method === "DATA");
  const isTwoSameMethod =
    segmentsToExport.length === 2 && segmentsToExport.every((seg) => seg.method === segmentsToExport[0].method);
  const driverMedianField = headroomDriver === "driver1" ? "driver1Median" : "driver2Median";
  const driverAvgField = headroomDriver === "driver1" ? "driver1Avg" : "driver2Avg";
  const driverPercentileField = headroomDriver === "driver1" ? "driver1Percentile" : "driver2Percentile";

  const formatMetricValue = (value: number | undefined, mode: AnalysisDriverType): string => {
    return formatMetric(value, mode);
  };

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatCombinedDriverTableData(segmentsToExport, data);
    const sortedRows = sortCombinedDriverRows(combinedRows, sortColumn, sortDirection);
    sortedRows.forEach((row) => {
      const uuid = row.data?.FxUniqueRowUuid || `${row.manualRange || ""}_${row.dataValue || ""}`;
      const rowData: (string | number)[] = [];
      rowData.push(row.manualRange || "");
      rowData.push(row.dataValue || "");
      rowData.push(
        editedMetrics[uuid]?.[driverMedianField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverMedianField], driverMode)
          : row.data?.[driverMedianField] != null
          ? formatMetricValue(row.data[driverMedianField], driverMode)
          : ""
      );
      rowData.push(
        editedMetrics[uuid]?.[driverAvgField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverAvgField], driverMode)
          : row.data?.[driverAvgField] != null
          ? formatMetricValue(row.data[driverAvgField], driverMode)
          : ""
      );
      rowData.push(
        editedMetrics[uuid]?.[driverPercentileField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverPercentileField], driverMode)
          : row.data?.[driverPercentileField] != null
          ? formatMetricValue(row.data[driverPercentileField], driverMode)
          : ""
      );
      worksheetData.push(rowData);
    });
  } else if (isManualOnly || isDataOnly) {
    const { rows } = formatDriverTableData(
      segmentsToExport,
      data || segmentsToExport[0]?.ranges.map((r) => r.data).filter((d): d is DriverMetricsResponse => !!d)
    );
    const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnly);
    sortedRows.forEach((rowSegments) => {
      const rangeData = rowSegments.find((r) => r.data);
      const uuid =
        rangeData?.data?.FxUniqueRowUuid ||
        rowSegments.map((seg) => `${seg.range || ""}_${seg.segmentValue || ""}`).join("_");
      const row: (string | number)[] = [];
      row.push(isManualOnly ? "Manual" : "Data");
      row.push(rowSegments[0].segmentValue || "");
      row.push(
        editedMetrics[uuid]?.[driverMedianField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverMedianField], driverMode)
          : rangeData?.data?.[driverMedianField] != null
          ? formatMetricValue(rangeData.data[driverMedianField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverAvgField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverAvgField], driverMode)
          : rangeData?.data?.[driverAvgField] != null
          ? formatMetricValue(rangeData.data[driverAvgField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverPercentileField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverPercentileField], driverMode)
          : rangeData?.data?.[driverPercentileField] != null
          ? formatMetricValue(rangeData.data[driverPercentileField], driverMode)
          : ""
      );
      worksheetData.push(row);
    });
  } else if (isTwoSameMethod) {
    const { rows } = formatDriverTableData(
      segmentsToExport,
      data ||
        segmentsToExport.flatMap((seg) => seg.ranges.map((r) => r.data).filter((d): d is DriverMetricsResponse => !!d))
    );
    const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnly);
    sortedRows.forEach((rowSegments) => {
      const rangeData = rowSegments.find((r) => r.data);
      const uuid =
        rangeData?.data?.FxUniqueRowUuid ||
        rowSegments.map((seg) => `${seg.range || ""}_${seg.segmentValue || ""}`).join("_");
      const row: (string | number)[] = [];
      const firstSegmentValue =
        rowSegments.find((r) => r.segmentIndex === segmentsToExport[0].index)?.segmentValue ||
        rowSegments.find((r) => r.segmentIndex === segmentsToExport[0].index)?.range ||
        "";
      const secondSegmentValue =
        rowSegments.find((r) => r.segmentIndex === segmentsToExport[1].index)?.segmentValue ||
        rowSegments.find((r) => r.segmentIndex === segmentsToExport[1].index)?.range ||
        "";
      row.push(firstSegmentValue);
      row.push(secondSegmentValue);
      row.push(
        editedMetrics[uuid]?.[driverMedianField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverMedianField], driverMode)
          : rangeData?.data?.[driverMedianField] != null
          ? formatMetricValue(rangeData.data[driverMedianField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverAvgField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverAvgField], driverMode)
          : rangeData?.data?.[driverAvgField] != null
          ? formatMetricValue(rangeData.data[driverAvgField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverPercentileField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverPercentileField], driverMode)
          : rangeData?.data?.[driverPercentileField] != null
          ? formatMetricValue(rangeData.data[driverPercentileField], driverMode)
          : ""
      );
      worksheetData.push(row);
    });
  } else {
    const { rows } = formatDriverTableData(
      segmentsToExport,
      data ||
        segmentsToExport.flatMap((seg) => seg.ranges.map((r) => r.data).filter((d): d is DriverMetricsResponse => !!d))
    );
    const sortedRows = sortDriverRows(rows, sortColumn, sortDirection, isDataOnly);
    sortedRows.forEach((rowSegments) => {
      const rangeData = rowSegments.find((r) => r.data);
      const uuid =
        rangeData?.data?.FxUniqueRowUuid ||
        rowSegments.map((seg) => `${seg.range || ""}_${seg.segmentValue || ""}`).join("_");
      const row: (string | number)[] = [];
      rowSegments.forEach((range) => {
        row.push(range.range || "");
        row.push(range.segmentValue || "");
      });
      row.push(
        editedMetrics[uuid]?.[driverMedianField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverMedianField], driverMode)
          : rangeData?.data?.[driverMedianField] != null
          ? formatMetricValue(rangeData.data[driverMedianField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverAvgField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverAvgField], driverMode)
          : rangeData?.data?.[driverAvgField] != null
          ? formatMetricValue(rangeData.data[driverAvgField], driverMode)
          : ""
      );
      row.push(
        editedMetrics[uuid]?.[driverPercentileField] !== undefined
          ? formatMetricValue(editedMetrics[uuid][driverPercentileField], driverMode)
          : rangeData?.data?.[driverPercentileField] != null
          ? formatMetricValue(rangeData.data[driverPercentileField], driverMode)
          : ""
      );
      worksheetData.push(row);
    });
  }
  return worksheetData;
};
/**
 * Builds row data for the Excel worksheet for HeadroomFinalTable.
 * @param segments - Array of headroom table segments to export.
 * @param data - Array of HeadroomFinalResponse data from the backend.
 * @param driverMode - Analysis mode for the driver (e.g., PERCENTAGE).
 * @param sortColumn - Column to sort by (default: "range").
 * @param sortDirection - Sorting direction (default: "asc").
 * @returns Array of row data for the Excel worksheet.
 */
export const buildHeadroomFinalRowData = (
  segments: HeadroomTableSegment[],
  data: HeadroomFinalResponse[] | undefined,
  driverMode: AnalysisDriverType,
  sortColumn: string = "range",
  sortDirection: "asc" | "desc" = "asc"
): (string | number)[][] => {
  const worksheetData: (string | number)[][] = [];
  const hasOneDataAndOneManualSegment =
    segments.length === 2 &&
    segments.some((seg) => seg.method === "DATA") &&
    segments.some((seg) => seg.method === "MANUAL");
  const isManualOnly = segments.length === 1 && segments.every((seg) => seg.method === "MANUAL");
  const isDataOnly = segments.length === 1 && segments.every((seg) => seg.method === "DATA");
  const isTwoSameMethod = segments.length === 2 && segments.every((seg) => seg.method === segments[0].method);

  if (hasOneDataAndOneManualSegment) {
    const { combinedRows } = formatHeadroomCombinedTableData(segments, data);
    const sortedCombinedRows = sortHeadroomCombinedRows(combinedRows, sortColumn, sortDirection);
    sortedCombinedRows.forEach((row) => {
      const rowData: (string | number)[] = [];
      rowData.push(row.manualRange || "");
      rowData.push(row.dataValue || "");
      rowData.push(row.data?.totalCustomers ?? "");
      rowData.push(row.data?.totalProspects ?? "");

      rowData.push(row.data?.penetration != null ? `${row.data.penetration.toFixed(2)}%` : "");
      rowData.push(formatMetric(row.data?.driverInitial, "COLUMN"));
      rowData.push(formatMetric(row.data?.driverFinal, "COLUMN"));
      rowData.push(formatMetric(row.data?.headroom, "COLUMN"));

      rowData.push(row.data?.headroomPercent != null ? `${row.data.headroomPercent.toFixed(2)}%` : "");

      rowData.push(row.data?.increasedPenetration != null ? `${row.data.increasedPenetration.toFixed(2)}%` : "");
      rowData.push(row.data?.totalCustomersAfter ?? "");
      rowData.push(formatMetric(row.data?.totalDriverInitial, "COLUMN"));
      rowData.push(formatMetric(row.data?.totalDriverFinal, "COLUMN"));
      rowData.push(formatMetric(row.data?.totalHeadroom, "COLUMN"));
      worksheetData.push(rowData);
    });
  } else {
    const { rows } = formatHeadroomTableData(
      segments,
      data || segments.flatMap((seg) => seg.ranges.map((r) => r.data).filter((d): d is HeadroomFinalResponse => !!d))
    );
    const sortedRows = sortHeadroomRows(rows, sortColumn, sortDirection, isDataOnly);
    sortedRows.forEach((rowSegments) => {
      const row: (string | number)[] = [];
      const rangeData = rowSegments.find((r) => r.data);
      if (isManualOnly) {
        row.push("Manual");
        row.push(rowSegments[0].segmentValue ?? "");
      } else if (isDataOnly && segments.length === 1) {
        row.push("Data");
        row.push(rowSegments[0].segmentValue ?? "");
      } else if (isTwoSameMethod) {
        const firstSegmentValue = rowSegments.find((r) => r.segmentIndex === segments[0].index)?.segmentValue || "";
        const secondSegmentValue = rowSegments.find((r) => r.segmentIndex === segments[1].index)?.segmentValue || "";
        row.push(firstSegmentValue);
        row.push(secondSegmentValue);
      } else {
        rowSegments.forEach((range) => {
          row.push(range.range ?? "");
          row.push(range.segmentValue ?? "");
        });
      }
      row.push(rangeData?.data?.totalCustomers ?? "");
      row.push(rangeData?.data?.totalProspects ?? "");

      row.push(rangeData?.data?.penetration != null ? `${rangeData.data.penetration.toFixed(2)}%` : "");
      row.push(formatMetric(rangeData?.data?.driverInitial, driverMode));
      row.push(formatMetric(rangeData?.data?.driverFinal, driverMode));
      row.push(formatMetric(rangeData?.data?.headroom, driverMode));

      row.push(rangeData?.data?.headroomPercent != null ? `${rangeData.data.headroomPercent.toFixed(2)}%` : "");

      row.push(
        rangeData?.data?.increasedPenetration != null ? `${rangeData.data.increasedPenetration.toFixed(2)}%` : ""
      );
      row.push(rangeData?.data?.totalCustomersAfter ?? "");
      row.push(formatMetric(rangeData?.data?.totalDriverInitial, driverMode));
      row.push(formatMetric(rangeData?.data?.totalDriverFinal, driverMode));
      row.push(formatMetric(rangeData?.data?.totalHeadroom, driverMode));
      worksheetData.push(row);
    });
  }
  return worksheetData;
};
